import { DetailHeader } from '@web/components/detail-view';
import React, { FC, ReactNode, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useSearchParams } from 'react-router-dom';
import { StyleSheet } from 'react-native';
import { withVersion } from './version.utils';
import { Button, Icon, TextBadge, Typography } from '@packages/ui/shared';
import {
    PublicationActionsEnum,
    PublicationForm,
    PublicationGroupStatus,
    PublicationStatusEnum,
    statusVariants,
    StisPublicationAction,
} from '@web/vin-sessions/types';
import { SelectPopover } from '@web/administration/views/worksheets/components/selectPopover';
import { getPublicationsListPath } from './publication-list-filter.util';

interface StisPublicationFormHeaderProps {
    publicationFormId?: string;
    publicationStatus?: PublicationStatusEnum;
    publicationInfo: PublicationForm;
    handleDuplicatePublication: (publicationFormId?: string, pubVersion?: number) => void;
    statusOfPublication: {
        isArchivedPublication: boolean;
        isPublishedPublication: boolean;
        isDraftPublicaiton: boolean;
        isInReviewPublication: boolean;
    };
    setShowDeletePublication: React.Dispatch<React.SetStateAction<boolean>>;
    fileUploadStatus: boolean;
    autosaveIndicator: ReactNode;
}

const styles = StyleSheet.create({
    headerSubtitle: {
        marginRight: 8,
    },
});

export const StisPublicationFormHeader: FC<StisPublicationFormHeaderProps> = ({
    publicationFormId,
    publicationStatus,
    publicationInfo,
    handleDuplicatePublication,
    setShowDeletePublication,
    statusOfPublication,
    fileUploadStatus,
    autosaveIndicator,
}) => {
    const { t } = useTranslation();
    const [searchParams] = useSearchParams();
    const action = searchParams.get('action') as StisPublicationAction;
    const status = publicationInfo.status as PublicationGroupStatus;
    const [selectedStatus, setSelectedStatus] = useState<PublicationGroupStatus>(PublicationGroupStatus.Draft);
    const [showStatusModal, setShowStatusModal] = useState(false);

    useEffect(() => {
        setSelectedStatus(status);
    }, [status]);

    const actions = {
        addVersion: t('stisPublications:actions.addVersion', 'Add new Version'),
        delete: t('stisPublications:actions.delete', 'Delete'),
    };

    const actionMenu = useMemo(() => {
        const menu: { label: string; value: PublicationActionsEnum; action: () => void }[] = [];
        const addVersion = {
            label: actions.addVersion,
            value: PublicationActionsEnum.Duplicate,
            action: () => handleDuplicatePublication(publicationFormId, publicationInfo?.pubVersion),
        };
        const deleteAction = {
            label: actions.delete,
            value: PublicationActionsEnum.Delete,
            action: () => {
                setShowDeletePublication(true);
                setShowStatusModal(false);
            },
        };

        if (status !== PublicationGroupStatus.Draft) {
            menu.push(addVersion);
        }
        menu.push(deleteAction);
        return menu;
    }, [
        actions.delete,
        actions.addVersion,
        status,
        publicationFormId,
        publicationInfo?.pubVersion,
        handleDuplicatePublication,
    ]);

    const isMenuDisabled =
        action === StisPublicationAction.New ||
        action === StisPublicationAction.AddVersion ||
        statusOfPublication.isInReviewPublication ||
        statusOfPublication.isArchivedPublication;

    const renderStatusMenu = () => (
        <SelectPopover
            key="status-menu"
            isOpen={showStatusModal}
            options={actionMenu.map((menu) => (
                <Button
                    key={menu.value}
                    variant="ghost-gray"
                    onPress={menu.action}
                    alignTitle="left"
                    title={menu.label}
                />
            ))}
            setShow={setShowStatusModal}
        >
            <div>
                <Button
                    variant="link"
                    onPress={() => setShowStatusModal((prev) => !prev)}
                    disabled={isMenuDisabled || fileUploadStatus}
                >
                    <Icon
                        size={30}
                        name="more-dots-vertical"
                        color="blueOne"
                        raisedContainerStyle={{ width: 22, height: 22 }}
                    />
                </Button>
            </div>
        </SelectPopover>
    );

    const headerRightButtons = [
        <React.Fragment key="autosave-indicator">{autosaveIndicator}</React.Fragment>,
        renderStatusMenu(),
    ];

    const breadcrumbs = [
        {
            to: '/administration',
            title: t('stisPublications:breadcrumbs.adminHub', 'Administration Hub'),
        },
        {
            to: getPublicationsListPath(publicationStatus),
            title: t('stisPublications:breadcrumbs.stisPublications', 'STIS Publications'),
        },
        {
            to: publicationFormId
                ? `/administration/publication/${publicationFormId}`
                : '/administration/publication/new',
            title: publicationFormId
                ? action === StisPublicationAction.Edit
                    ? t('stisPublications:breadcrumbs.editPublication', 'Edit publication')
                    : action === StisPublicationAction.AddVersion
                      ? t('stisPublications:breadcrumbs.addNewVersion', 'Add a new version')
                      : t('stisPublications:breadcrumbs.addNewPublication', 'Add new publication')
                : t('stisPublications:breadcrumbs.addNewPublication', 'Add new publication'),
        },
    ];

    return (
        <DetailHeader
            breadcrumbs={breadcrumbs}
            subtitle={
                <>
                    <Typography variant="h3" style={styles.headerSubtitle}>
                        {withVersion(publicationInfo?.title, publicationInfo?.pubVersion)}
                    </Typography>
                    <TextBadge variant={statusVariants[selectedStatus]}>{selectedStatus}</TextBadge>
                </>
            }
            hasBack
            rightSideItems={headerRightButtons}
            disableBackButton={fileUploadStatus}
        />
    );
};
